import { styled } from "@mui/material/styles";
import { createContext, Fragment, useContext, useLayoutEffect, type ReactNode } from "react";
import { FOLDED_VALUE_ATTRIBUTE } from "_shared/_views/tables/table-column-widths";

/** What a folded column reads as on its host's second line. */
export type FoldedValue = { columnId: string; content: ReactNode };

/**
 * The values other columns folded onto the cell being drawn (#6711). The list table provides it around
 * a host column's cell; a {@link CondensedCell} inside reads it and adds the values to its second line.
 */
export const FoldedValuesContext = createContext<readonly FoldedValue[]>([]);

/**
 * Set by a host column whose cell is not itself a {@link CondensedCell} but may draw one inside: a name
 * cell that also draws an avatar or a link. The host gives such a cell a two-line cell of its own, and
 * a {@link CondensedCell} drawn inside calls this to say it shows the folded values, so the host's own
 * second line does not show them a second time.
 */
export const FoldedValuesClaimContext = createContext<(() => void) | undefined>(undefined);

/** Between the values that share a second line. */
const SEPARATOR = " · ";

const CellBox = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  minWidth: 0,
  width: "100%",
});

const truncates = {
  display: "block",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

const TitleLine = styled("div")({ ...truncates, width: "100%" });

const SecondaryLine = styled("div")(({ theme }) => ({
  color: theme.palette.text.secondary,
  display: "flex",
  fontSize: theme.typography.body2.fontSize,
  lineHeight: 1.4,
  minWidth: 0,
  width: "100%",
}));

const SecondaryPart = styled("span")({ ...truncates, flex: "0 1 auto" });

// The folded values keep their whole width, and the cell's own second line takes what is left, down to
// a few characters: a long description cannot push a folded value out of sight, since the folded value
// has nowhere else to show. Shrinking both, however unevenly, still took a fraction of a pixel from the
// folded value at 768px, and that was enough to end "2 fees" in an ellipsis.
const OWN_MINIMUM = "2.5em";
const OwnPart = styled(SecondaryPart)({ minWidth: OWN_MINIMUM });
const FoldedPart = styled(SecondaryPart)({ flexShrink: 0, maxWidth: "100%" });
const FoldedAfterOwnPart = styled(FoldedPart)({ maxWidth: `calc(100% - ${OWN_MINIMUM})` });

function joined(values: readonly ReactNode[]): ReactNode {
  return values.map((value, index) => (
    // The values are a fixed list for the render, so their place in it is their identity.
    <Fragment key={index}>
      {index > 0 && SEPARATOR}
      {value}
    </Fragment>
  ));
}

function present(value: ReactNode): boolean {
  return value !== undefined && value !== null && value !== false && value !== "";
}

/**
 * A list table's two-line cell: a title, and a secondary line under it (#6711).
 *
 * The secondary line carries the values a row is read by but that do not earn a column of their own:
 * a proposal's creator under its name. A column too low in priority for a narrow pane folds onto the same
 * line (`meta.fold`), after the cell's own values, so its value stays on screen rather than going
 * sideways off it.
 *
 * Both lines stay on one line and end in an ellipsis when they do not fit. The table's own tooltip
 * (`useTableCellTooltip`) shows the whole of a clipped line on hover, so no cell needs a tooltip of its
 * own. A folded value is marked so the fit does not measure it as this column's content.
 *
 * `after` is drawn under both lines, for a control that is not a value: a link to compare a request's
 * proposals.
 */
export default function CondensedCell(
  props: Readonly<{
    title: ReactNode;
    /** The cell's own second line. Several values are joined with " · "; empty ones are left out. */
    secondary?: ReactNode | readonly ReactNode[];
    after?: ReactNode;
    "data-testid"?: string;
  }>,
) {
  const folded = useContext(FoldedValuesContext);
  const claimFoldedValues = useContext(FoldedValuesClaimContext);
  const own = (Array.isArray(props.secondary) ? props.secondary : [props.secondary]).filter(present);
  const foldedContent = folded.map((value) => value.content).filter(present);
  const showsFolded = foldedContent.length > 0;
  // Before paint, so a host that wraps this cell never draws the values beside it for a frame.
  useLayoutEffect(() => {
    if (!showsFolded) return;
    claimFoldedValues?.();
  }, [claimFoldedValues, showsFolded]);
  const Folded = own.length > 0 ? FoldedAfterOwnPart : FoldedPart;

  return (
    <CellBox data-testid={props["data-testid"]}>
      <TitleLine>{props.title}</TitleLine>
      {(own.length > 0 || foldedContent.length > 0) && (
        <SecondaryLine>
          {own.length > 0 && <OwnPart>{joined(own)}</OwnPart>}
          {foldedContent.length > 0 && (
            <Folded {...{ [FOLDED_VALUE_ATTRIBUTE]: "" }} data-testid="folded-values">
              {own.length > 0 && SEPARATOR}
              {joined(foldedContent)}
            </Folded>
          )}
        </SecondaryLine>
      )}
      {props.after}
    </CellBox>
  );
}
